import { useQuery } from "react-query";
import { API_URL, APIError, APIResponse, get } from "../api";

/** Keys mirror App\Services\Kyc\KycRequirements' constants. */
export type KycRequirementKey =
  | "email"
  | "name"
  | "date_of_birth"
  | "nin"
  | "address"
  | "passport"
  | "residence";

export interface KycRequirement {
  key: KycRequirementKey;
  label: string;
  description: string;
  satisfied: boolean;
  meta?: { postcode_required?: boolean; country?: string | null };
}

export interface KycRequirementsResult {
  residence_country: string | null;
  residence_source: string;
  is_international: boolean;
  satisfied: boolean;
  requirements: KycRequirement[];
  missing: KycRequirementKey[];
}

const empty: KycRequirementsResult = {
  residence_country: null,
  residence_source: "unknown",
  is_international: false,
  satisfied: false,
  requirements: [],
  missing: [],
};

/**
 * What the signed-in customer still owes.
 *
 * `context` picks which rule set to ask about: account setup, or the extra
 * checks required when the customer requests Pay in 3 for an international
 * flight.
 *
 * Both come from the same server-side service, so the dashboard checklist, the
 * compliance screen and the financing gate cannot disagree. createOrder
 * enforces the Pay in 3 gate regardless of what this returns.
 */
function useKycRequirements(
  context: "onboarding" | "international_flight" = "onboarding",
  enabled = true
) {
  const url =
    context === "international_flight"
      ? `${API_URL}/aviation/international/v2/kyc-requirements`
      : `${API_URL}/account/compliance/requirements`;

  const query = useQuery<APIResponse<KycRequirementsResult>, APIError>({
    queryKey: ["kyc-requirements", context],
    queryFn: () =>
      get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });

  return {
    requirements: query.data?.data ?? empty,
    isLoadingRequirements: query.isLoading,
    isRequirementsError: query.isError,
    refetchRequirements: query.refetch,
  };
}

export default useKycRequirements;
